import { useState } from 'react';
import {
  Card, CardContent, Typography, Button, Box,
  Alert, Paper, RadioGroup, FormControlLabel, Radio,
  FormControl, FormLabel, LinearProgress, Chip
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { assessmentApi } from '../utils/api';
import { Question, FollowupQuestion, FollowupChoice } from '../types';
import MathInput from '../components/MathInput';
import FollowupDialog from '../components/FollowupDialog';

export default function SkippedReviewPage() {
    // Show result feedback state
    const [showResult, setShowResult] = useState(false);
  const { updateStatus } = useSession();
  const { data: skippedQuestions = [] } = useQuery<Question[]>({
    queryKey: ['assessment', 'skipped'],
    queryFn: () => assessmentApi.getSkippedQuestions(),
  });
  const [currentIndex, setCurrentIndex] = useState(-1); // -1 for intro screen
  const [answer, setAnswer] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [showSkipReasons, setShowSkipReasons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Followup dialog state
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState<FollowupQuestion | null>(null);
  const [followupChoices, setFollowupChoices] = useState<FollowupChoice[]>([]);

  const currentQuestion = currentIndex >= 0 ? skippedQuestions[currentIndex] : null;

  const handleBeginReview = () => {
    setCurrentIndex(0);
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim() || !currentQuestion) return;

    setSubmitting(true);
    setError('');

    try {
      const result = await assessmentApi.submitAnswer(
        currentQuestion.question_id,
        answer.trim(),
        new Date().toISOString(),
        new Date().toISOString()
      );

      if (result.followup_required && result.followup_question) {
        setFollowupQuestion(result.followup_question);
        setFollowupChoices(result.followup_choices);
        setShowFollowup(true);
      } else {
        setShowResult(true);
        setTimeout(() => {
          setShowResult(false);
          moveToNext();
        }, 1500);
      }
    } catch {
      setError('Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowupSubmit = async (selectedFollowup: string[], followupFreeResponse: string) => {
    if (!currentQuestion || !followupQuestion) return;

    const followupType = followupQuestion.followup_type;

    await assessmentApi.submitFollowup(
      currentQuestion.question_id,
      followupType === 'FR' ? [] : selectedFollowup,
      followupType === 'FR' ? followupFreeResponse : undefined
    );
    setShowFollowup(false);
    setShowResult(true);
    setTimeout(() => {
      setShowResult(false);
      moveToNext();
    }, 1500);
  };

  const handleSkipAgain = () => {
    setShowSkipReasons(true);
  };

  const handleSubmitSkipReason = async () => {
    if (!skipReason || !currentQuestion) return;

    setSubmitting(true);
    try {
      await assessmentApi.submitSkipReason(currentQuestion.question_id, skipReason);
      setShowSkipReasons(false);
      setSkipReason('');
      moveToNext();
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const moveToNext = () => {
    setAnswer('');
    if (currentIndex < skippedQuestions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      updateStatus('feedback_teaser');
    }
  };

  // Intro screen
  if (currentIndex === -1) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Before We Finish Your Results
          </Typography>

          <Typography variant="body1" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
            You left {skippedQuestions.length} question{skippedQuestions.length !== 1 ? 's' : ''} unanswered.
            We always revisit skipped items before generating results.
          </Typography>

          <Typography variant="body1" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
            You may attempt them now or confirm that you want to skip them.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleBeginReview}
            sx={{ mt: 2, px: 6 }}
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Question {currentIndex + 1} of {skippedQuestions.length}
              </Typography>
              <Chip
                label={`${Math.round(((currentIndex + 1) / skippedQuestions.length) * 100)}% Complete`}
                color="primary"
                size="small"
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={((currentIndex + 1) / skippedQuestions.length) * 100}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Typography variant="h5" component="h1" gutterBottom color="primary">
          Review Skipped Question {currentIndex + 1} of {skippedQuestions.length}
        </Typography>

        {/* Question */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom fontWeight={800}>
            Question {currentQuestion.question_number}
          </Typography>
          <Box sx={{ fontSize: '1.1rem', '& p': { mt: 0, mb: 1 } }}>
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[rehypeKatex]}
            >
              {currentQuestion.question_text}
            </ReactMarkdown>
          </Box>

            {/* Display graph if available */}
            {currentQuestion.graph_uri && currentQuestion.graph_uri.trim() !== '' && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={currentQuestion.graph_uri?.startsWith('/api') 
                    ? `${import.meta.env.VITE_API_URL?.replace(/\/api$/, '')}${currentQuestion.graph_uri}` 
                    : currentQuestion.graph_uri}
                  alt={`Graph for question ${currentQuestion.question_number}. ${currentQuestion.notes}`}
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    boxShadow: 1,
                  }}
                  aria-label={`Graph for question ${currentQuestion.question_number}. ${currentQuestion.notes}`}
                />
              </Box>
            )}
        </Paper>

        {!showSkipReasons ? (
          <>
            {showResult ? (
              <Alert
                severity="info"
                sx={{ my: 2 }}
                role="status"
                aria-live="polite"
              >
                Answer recorded. Moving to next question...
              </Alert>
            ) : (
              <>
                {/* Answer Input */}
                <MathInput
                  value={answer}
                  onChange={(value) => setAnswer(value)}
                  disabled={submitting}
                  label="Your Answer"
                />
                <Box sx={{ mb: 3 }} />

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }} role="alert">
                    {error}
                  </Alert>
                )}

                {/* Action Buttons */}
                <Box display="flex" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    onClick={handleSkipAgain}
                    disabled={submitting}
                  >
                    Skip Again
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitAnswer}
                    disabled={!answer.trim() || submitting}
                    endIcon={<NavigateNextIcon />}
                  >
                    {submitting ? 'Submitting...' : 'Submit Answer'}
                  </Button>
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            {/* Skip Reason Selection */}
            <Alert severity="info" sx={{ mb: 3 }}>
              Please tell us why you're skipping this question. This helps us understand your needs better.
            </Alert>

            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Why did you skip this question?</FormLabel>
              <RadioGroup
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
              >
                <FormControlLabel
                  value="did_not_know"
                  control={<Radio />}
                  label="I did not know how to do this"
                />
                <FormControlLabel
                  value="did_not_understand"
                  control={<Radio />}
                  label="I did not understand what was being asked"
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label="Other reason or prefer not to say"
                />
              </RadioGroup>
            </FormControl>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} role="alert">
                {error}
              </Alert>
            )}

            <Box display="flex" justifyContent="space-between">
              <Button
                variant="outlined"
                onClick={() => setShowSkipReasons(false)}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitSkipReason}
                disabled={!skipReason || submitting}
                endIcon={<NavigateNextIcon />}
              >
                {submitting ? 'Submitting...' : 'Continue'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
    
    {/* Followup Dialog */}
    <FollowupDialog
      open={showFollowup}
      followupQuestion={followupQuestion}
      followupChoices={followupChoices}
      originalQuestionGraphUri={currentQuestion?.graph_uri}
      originalQuestionGraphAlt={`Graph for question ${currentQuestion?.question_number}. ${currentQuestion?.notes || ''}`.trim()}
      onSubmit={handleFollowupSubmit}
      onError={(err) => setError(err)}
    />
    </>
  );
}
